import React, { useState, FormEvent, ChangeEvent } from 'react';
import { APP_TITLE } from '../constants';
import { auth } from '../services/firebase';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  sendPasswordResetEmail, 
  signInWithPopup, 
  GoogleAuthProvider,
  AuthError 
} from 'firebase/auth';
import Button from './Button';
import TextInput from './TextInput';

interface LoginScreenProps {
  onAuthError: (error: Error) => void;
}

// Use a direct hosted URL to prevent errors with long data URIs.
const robotImageUrl = "https://firebasestorage.googleapis.com/v0/b/history-essay-bot.firebasestorage.app/o/Holly%20AI%20Marker%20%202.jpeg?alt=media&token=3697c940-a34c-441b-a871-08751d13da5a";

const LoginScreen: React.FC<LoginScreenProps> = ({ onAuthError }) => {
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  
  // Google sign-in state
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const handleAuthAction = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (isSigningUp && password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsLoading(true);
    try {
      // Debug Firebase auth state with more comprehensive logging
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      console.log("🔧 Auth attempt details:", {
        hostname: window.location.hostname,
        port: window.location.port,
        protocol: window.location.protocol,
        href: window.location.href,
        isLocalhost,
        authDomain: auth.config.authDomain,
        projectId: auth.app.options.projectId
      });
      
      // Check if auth service is properly initialized
      if (!auth || !auth.app) {
        throw new Error("Firebase Auth not properly initialized");
      }

      if (isLocalhost) {
        console.log("🏠 Localhost detected - checking auth configuration...");
        console.log("🔑 Auth config:", {
          apiKey: auth.app.options.apiKey ? `${auth.app.options.apiKey.substring(0, 10)}...` : 'MISSING',
          authDomain: auth.app.options.authDomain,
          projectId: auth.app.options.projectId
        });
      }

      if (isSigningUp) {
        console.log("📝 Attempting to create user account...");
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        console.log("🔐 Attempting to sign in user...");
        await signInWithEmailAndPassword(auth, email, password);
      }
      console.log("✅ Authentication successful");
      // On success, the onAuthStateChanged listener in App.tsx will take over.
    } catch (err: unknown) {
      // Log the complete error for debugging
      console.error("Authentication error:", err);
      
      // Check for a specific configuration error first. If found, pass it up to the parent to handle with a modal.
      const error = err as AuthError;
      const errorCode = error?.code || '';
      const errorMessage = error?.message?.toLowerCase() || '';
      
      console.log("Auth error details - Code:", errorCode, "Message:", errorMessage);
      console.log("Full error object:", JSON.stringify(error));

      if (errorMessage.includes('requests from referer') && errorMessage.includes('are blocked')) {
        if (onAuthError) onAuthError(error as Error);
        setIsLoading(false); // Stop loading indicator
        return; // Halt further error processing here
      }

      // Check for API key issues
      if (errorCode === 'auth/api-key-not-valid' || errorMessage.includes('api key not valid')) {
        if (onAuthError) onAuthError(error as Error);
        setIsLoading(false);
        return;
      }

      let friendlyMessage = "An unexpected error occurred.";
      switch (errorCode) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-login-credentials':
          friendlyMessage = "Invalid email or password.";
          break;
        case 'auth/email-already-in-use':
          friendlyMessage = "An account with this email already exists.";
          break;
        case 'auth/weak-password':
          friendlyMessage = "Password should be at least 6 characters long.";
          break;
        case 'auth/invalid-email':
          friendlyMessage = "Please enter a valid email address.";
          break;
        case 'auth/api-key-not-valid':
          friendlyMessage = "Authentication configuration error. Please contact support.";
          break;
        default:
          console.error("Firebase Auth Error:", err);
          break;
      }
      setError(friendlyMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleForm = () => {
    setIsSigningUp(!isSigningUp);
    setError(null);
    setEmail('');
    setPassword('');
    setConfirmPassword('');
  };

  const handleForgotPassword = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setResetError(null);
    setResetSuccess(false);

    if (!resetEmail.trim()) {
      setResetError("Please enter your email address.");
      return;
    }

    setIsResettingPassword(true);
    try {
      await sendPasswordResetEmail(auth, resetEmail.trim());
      setResetSuccess(true);
      setResetEmail('');
    } catch (err: unknown) {
      const error = err as AuthError;
      const errorCode = error?.code || '';
      
      let friendlyMessage = "An unexpected error occurred.";
      switch (errorCode) {
        case 'auth/user-not-found':
          friendlyMessage = "No account found with this email address.";
          break;
        case 'auth/invalid-email':
          friendlyMessage = "Please enter a valid email address.";
          break;
        case 'auth/too-many-requests':
          friendlyMessage = "Too many requests. Please try again later.";
          break;
        default:
          console.error("Password reset error:", err);
          break;
      }
      setResetError(friendlyMessage);
    } finally {
      setIsResettingPassword(false);
    }
  };

  const closeForgotPasswordModal = () => {
    setShowForgotPassword(false);
    setResetEmail('');
    setResetError(null);
    setResetSuccess(false);
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setIsGoogleLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      // Add custom parameters for better UX
      provider.setCustomParameters({
        prompt: 'select_account'
      });

      console.log("🔐 Attempting Google Sign-in...");
      const result = await signInWithPopup(auth, provider);
      console.log("✅ Google Sign-in successful:", result.user.email);
      // On success, the onAuthStateChanged listener in App.tsx will take over.
    } catch (err: unknown) {
      console.error("Google Sign-in error:", err);
      
      const error = err as AuthError;
      const errorCode = error?.code || '';
      const errorMessage = error?.message?.toLowerCase() || '';
      
      // Handle popup closed by user
      if (errorCode === 'auth/popup-closed-by-user') {
        console.log("User closed the Google sign-in popup");
        setIsGoogleLoading(false);
        return; // Don't show error for user-cancelled action
      }
      
      // Check for configuration errors
      if (errorMessage.includes('requests from referer') && errorMessage.includes('are blocked')) {
        if (onAuthError) onAuthError(error as Error);
        setIsGoogleLoading(false);
        return;
      }

      let friendlyMessage = "Google sign-in failed. Please try again.";
      switch (errorCode) {
        case 'auth/popup-blocked':
          friendlyMessage = "Popup was blocked. Please allow popups for this site and try again.";
          break;
        case 'auth/cancelled-popup-request':
          friendlyMessage = "Sign-in was cancelled. Please try again.";
          break;
        case 'auth/network-request-failed':
          friendlyMessage = "Network error. Please check your connection and try again.";
          break;
        case 'auth/invalid-api-key':
          friendlyMessage = "Configuration error. Please contact support.";
          break;
        default:
          console.error("Google Auth Error:", err);
          break;
      }
      setError(friendlyMessage);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <div className="w-full max-w-5xl mx-auto bg-card rounded-lg shadow-large overflow-hidden md:flex border border-border">
      {/* Image Pane */}
      <div className="hidden md:block md:w-1/2 bg-gradient-to-br from-primary to-[#1a1040] relative">
        <div className="absolute inset-0 bg-black/10"></div>
        <img 
            src={robotImageUrl}
            alt="Abstract illustration of a stylized letter H with circuit patterns, representing an AI marking assistant."
            className="w-full h-full object-cover relative z-10 mix-blend-overlay"
        />
        <div className="absolute inset-0 flex items-center justify-center z-20">
          <div className="text-center text-primary-foreground">
            <div className="text-6xl font-bold mb-4">A°</div>
            <p className="text-xl font-medium">AI-Powered Essay Marking</p>
          </div>
        </div>
      </div>

      {/* Form Pane */}
      <div className="w-full md:w-1/2 p-8 sm:p-12 space-y-6 flex flex-col justify-center bg-card">
        <div className="text-center">
          <h2 className="text-lg font-medium text-muted-foreground">Welcome to</h2>
          <h1 className="text-3xl font-bold text-foreground mt-2">{APP_TITLE}</h1>
        </div>
        <p className="text-center text-sm text-muted-foreground">
          {isSigningUp 
            ? "Create an account to save your work." 
            : "Sign in to access your assessments."}
        </p>

        <form onSubmit={handleAuthAction} className="space-y-4">
          <TextInput
            label="Email Address"
            id="email"
            type="email"
            value={email}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
          <TextInput
            label="Password"
            id="password"
            type="password"
            value={password}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
            required
            autoComplete={isSigningUp ? "new-password" : "current-password"}
          />
          {isSigningUp && (
            <TextInput
              label="Confirm Password"
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
          )}
          {error && (
              <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-md text-sm">
                {error}
              </div>
          )}
          <div className="pt-4">
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full"
              isLoading={isLoading}
              loadingText={isSigningUp ? 'Creating Account...' : 'Signing In...'}
            >
              {isSigningUp ? 'Create Account' : 'Sign In'}
            </Button>
          </div>
        </form>
        
        {/* Google Sign In Button */}
        <div className="pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleSignIn}
            disabled={isLoading || isGoogleLoading}
            className="w-full"
          >
            {isGoogleLoading ? (
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-3 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Signing in with Google...
              </div>
            ) : (
              <>
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </>
            )}
          </Button>
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-card text-muted-foreground">or</span>
          </div>
        </div>
        
        <div className="text-center space-y-2">
          <Button
            variant="link"
            onClick={toggleForm}
            disabled={isLoading}
          >
            {isSigningUp
              ? "Already have an account? Sign In"
              : "Don't have an account? Sign Up"}
          </Button>
          
          {!isSigningUp && (
            <div>
              <Button
                variant="ghost"
                onClick={() => setShowForgotPassword(true)}
                disabled={isLoading}
                className="text-muted-foreground hover:text-foreground"
              >
                Forgot your password?
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-card rounded-lg shadow-large max-w-md w-full p-6 border border-border">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-foreground">Reset Password</h3>
              <button
                onClick={closeForgotPasswordModal}
                disabled={isResettingPassword}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>

            {resetSuccess ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto">
                  <svg className="w-8 h-8 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-medium text-foreground mb-2">Check your email</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    We've sent a password reset link to your email address. Please check your inbox and follow the instructions to reset your password.
                  </p>
                </div>
                <Button
                  onClick={closeForgotPasswordModal}
                  className="w-full"
                >
                  Done
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
                
                <TextInput
                  label="Email Address"
                  id="resetEmail"
                  type="email"
                  value={resetEmail}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setResetEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder="Enter your email address"
                />
                
                {resetError && (
                  <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-md text-sm">
                    {resetError}
                  </div>
                )}
                
                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    onClick={closeForgotPasswordModal}
                    variant="outline"
                    disabled={isResettingPassword}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isResettingPassword || !resetEmail.trim()}
                    className="flex-1"
                    isLoading={isResettingPassword}
                    loadingText="Sending..."
                  >
                    Send Reset Link
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginScreen;
